/**
 * Bot Cove tick-log narrative builder.
 *
 * The trading-engine emits a ``narrative`` string on every ``:bot``
 * envelope, but it's tied to internal strategy codenames (``S1``,
 * ``fib_15m``) which mean nothing to end-users. Instead of
 * post-processing the worker's string, we rebuild the narrative
 * UI-side from the structured fields (``decision``, ``reason``,
 * ``gate``, ``market_id``, ``metrics``) so the user reads
 * conversational lines in the bot's voice — no strategy concept, no
 * gate jargon, no raw numbers ever surface.
 *
 * Lines are localised: ``buildCoveNarrative`` takes the i18n ``t`` and
 * resolves ``cove.narrative.*`` keys, so the feed follows the user's
 * language. The worker's ``narrative`` field stays on the wire
 * untouched (admin tooling / logs) — the UI ignores it.
 */

type Decision = 'enter' | 'skip' | 'wait';

/** i18n translator — the subset of react-i18next's ``t`` used here. */
export type Translate = (key: string, opts?: Record<string, unknown>) => string;

export interface BotTickPayload {
  engine?: 'pm' | 'hl';
  decision?: Decision;
  reason?: string;
  /** Precise near-miss ring on a ``no_signal`` skip (S1 only).
   *  Mapped to a plain-language phrase; the raw gate name and its
   *  numeric ``gate_value``/``gate_threshold`` never surface. */
  gate?: string;
  market_id?: string;
  metrics?: {
    side?: string;
    limit_price?: number;
    entry_price?: number;
  };
  asset_price?: number;
}

/**
 * Pull a user-readable symbol out of a market_id.
 *   "btc-5m"   → "BTC"
 *   "eth-15m"  → "ETH"
 *   "BTC-PERP" → "BTC"
 *   "SOL"      → "SOL"
 */
export function symbolOf(market_id: string | undefined): string {
  if (!market_id) return '';
  return (market_id.split('-')[0] || '').toUpperCase();
}

function priceLabel(price: number | undefined, engine: BotTickPayload['engine']): string {
  if (typeof price !== 'number' || !Number.isFinite(price)) return '';
  // PM token prices live in [0, 1], render 2dp. HL marks are USD, integer-OK.
  return engine === 'hl' ? `$${Math.round(price)}` : price.toFixed(2);
}

/**
 * Precise skip gates → ``cove.narrative.*`` key. The engine tags a
 * ``no_signal`` skip with a ``gate`` (plus a numeric
 * ``gate_value``/``gate_threshold`` pair) — that detail powers admin
 * Mission Control, but a user doesn't need "edge 0.04 < 0.07". Several
 * gates collapse onto one friendly phrase. Gates with no user-meaningful
 * distinction (``dispatch``, ``regime``, ``setup_sn``) are intentionally
 * absent — they fall through to the generic "no signal yet" line.
 */
const GATE_KEY: Record<string, string> = {
  feed: 'gate_feed',
  min_edge: 'gate_odds',
  edge_band: 'gate_odds',
  min_fair_value: 'gate_odds',
  min_win_prob: 'gate_odds',
  signal_agreement: 'gate_signals_mixed',
  conflict: 'gate_signals_conflict',
  kelly_size: 'gate_stake_small',
  price_band: 'gate_price_band',
  max_delta: 'gate_price_far',
  max_churn: 'gate_choppy',
  entry_window: 'gate_timing',
  liq_events: 'gate_quiet',
  liq_notional: 'gate_quiet',
  side_ratio: 'gate_quiet',
  price_move: 'gate_quiet',
  stake: 'gate_bankroll',
};

function directionLabel(side: string | undefined, t: Translate): string {
  const s = (side ?? '').toUpperCase();
  if (s === 'UP' || s === 'LONG' || s === 'BUY') return t('cove.narrative.dir_up');
  if (s === 'DOWN' || s === 'SHORT' || s === 'SELL') return t('cove.narrative.dir_down');
  return '';
}

/**
 * Build the localised tick-log line for one ``:bot`` envelope. Always
 * returns a non-empty string — every branch resolves to a phrase.
 */
export function buildCoveNarrative(payload: BotTickPayload, t: Translate): string {
  const { decision, reason, gate, market_id, metrics, engine } = payload;
  const symbol = symbolOf(market_id);
  const n = (key: string, opts?: Record<string, unknown>) =>
    t(`cove.narrative.${key}`, opts);

  if (decision === 'enter') {
    const dir = directionLabel(metrics?.side, t);
    const price = priceLabel(metrics?.limit_price ?? metrics?.entry_price, engine);
    if (dir && price) return n('enter_priced', { dir, price });
    if (symbol) return n('enter_symbol', { symbol });
    return n('enter');
  }

  if (decision === 'wait') {
    switch (reason) {
      case 'in_flight':
        return n('in_flight');
      case 'cooldown':
        return n('cooldown');
      case 'matched_this_epoch':
        return n('matched');
      default:
        return n('waiting');
    }
  }

  // decision === 'skip' or unknown — treat as "looking, not biting"
  switch (reason) {
    case 'kill_switch':
      return n('kill_switch');
    case 'bankroll_insufficient':
      return n('bankroll');
    case 'no_setup':
      return symbol ? n('no_setup_symbol', { symbol }) : n('no_setup');
    case 'no_signal': {
      const gateKey = gate ? GATE_KEY[gate] : undefined;
      if (gateKey) return n(gateKey);
      return symbol ? n('no_signal_symbol', { symbol }) : n('no_signal');
    }
    case 'no_fill':
      return n('no_fill');
    case 'score_below_threshold':
      return n('score_low');
    case 'coin_cooldown':
      return symbol ? n('coin_cooldown_symbol', { symbol }) : n('coin_cooldown');
    default:
      return symbol ? n('pass_symbol', { symbol }) : n('pass');
  }
}
